import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Switch, Route } from 'react-router-dom';
import ConnectedDisplayAd from './DisplayAndVideo/ConnectedDisplayAd';
import ConnecteViewCampaigns from './ViewCampaigns/ConnectedViewCampaigns';
import { useAdvertisers } from '../../../Contexts/AdvertisersContext';
import { useProfile } from '../../../Contexts/UserProfileContext';
import usePost from '../../../Hooks/usePost';
import { useCampaigns } from '../../../Contexts/CampaignsContext';
import CreateACampaign from './DisplayAndVideo/CreateACampaign';
import useRequest from '../../../Hooks/useRequest';

const CampaignContainer = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
`;

const ConnectedCampaign = () => {
    const [pageIndex, setPageIndex] = useState(0);

    const handleNavButtonNext = () => {
        setPageIndex(pageIndex + 1);
    };
    const handleNavButtonBack = () => {
        setPageIndex(pageIndex - 1);
    };

    const [campaignData, setCampaignData] = useState({
        campaignTypeId: 0,
        campaignTypeCategoryId: 0,
        campaignTypeCategoryName: '',
        advertiserId: 0,
        agencyId: 0,
        campaignTypeName: '',
        startDate: '',
        endDate: '',
        campaignName: '',
        headline: '',
        headline2: '',
        headline3: '',
        headline4: '',
        headline5: '',
        headline6: '',
        description: '',
        description2: '',
        description3: '',
        description4: '',
        brandName: '',
        offerDescription: '',
        productDescription: '',
        redemptionInstructions: '',
        websiteURL: '',
        budget: 0,
        stripePaymentId: '',
        stripeCustomerId: '',
        googleCampaignKeywords: [],
        locations: [],
        file: {
            fileName: '',
            file: null,
        },
    });

    const [campaignPostData, setCampaignPostData] = useState({
        success: false,
        error: false,
        isLoading: false,
        message: [''],
    });

    const [campaignLocations, setCampaignLocations] = useState([]);

    const { post: postCreatePaymentIntent } = usePost('/stripe/createPaymentIntent');
    const { post: postNewGoogleCampaign } = usePost('/postNewGoogleCampaign');
    const { post: postNewPontiacCreative } = usePost('/postNewPontiacCreative');
    const { post: postNewPontiacCampaign } = usePost('/postNewPontiacCampaign');
    const { post: postNewMobileCamapign } = usePost('/uploadAndSendEmail');

    const { advertisers, setAdvertisers, currentAdvertiser, setCurrentAdvertiser } = useAdvertisers();
    const { profile } = useProfile();
    const { agency } = { ...profile };
    const agencyId = agency?.agencyId;
    const advertiserId = currentAdvertiser?.advertiserId;
    const stripeCustomerId = currentAdvertiser?.stripeCustomerId;
    const advertiserName = currentAdvertiser?.companyName;
    const shouldChargeCreditCard = currentAdvertiser?.chargeCreditCard;

    const { campaigns } = useCampaigns();

    const isGoogle = campaignData?.campaignTypeId === 1;
    const isProgrammatic = campaignData?.campaignTypeId === 26;
    const isMobile = campaignData?.campaignTypeId === 2;

    const resetFields = () => {
        setCampaignData({
            agencyId: agencyId,
            advertiserId: advertiserId,
            advertiserName: advertiserName,
            stripeCustomerId: stripeCustomerId,
            campaignTypeId: 0,
            campaignTypeCategoryId: 0,
            campaignTypeCategoryName: '',
            campaignTypeName: '',
            startDate: '',
            endDate: '',
            campaignName: '',
            headline: '',
            headline2: '',
            headline3: '',
            headline4: '',
            headline5: '',
            headline6: '',
            description: '',
            description2: '',
            description3: '',
            description4: '',
            brandName: '',
            offerDescription: '',
            productDescription: '',
            redemptionInstructions: '',
            websiteURL: '',
            budget: 0,
            googleCampaignKeywords: [],
            locations: [],
            file: {
                fileName: '',
                file: null,
            },
        });
    };

    useEffect(() => {
        resetFields();
    }, []);

    useEffect(() => {
        setCampaignData({
            ...campaignData,
            agencyId: agencyId,
            advertiserId: advertiserId,
            advertiserName: advertiserName,
            stripeCustomerId: stripeCustomerId,
        });
    }, [currentAdvertiser]);

    const { data } = useRequest(`/stripe/getCustomer/${campaignData?.stripeCustomerId}`);
    const defaultPaymentMethodId = data?.invoice_settings?.default_payment_method;

    const formatLocationData = () => {
        const array = [];

        if (isGoogle && campaignData?.locations?.length > 0) {
            campaignData.locations.forEach((item) => {
                if (item) {
                    array.push({
                        canonicalName: item.canonicalName,
                        reach: item.reach,
                        constantId: item.constantId,
                    });
                }
            });
            setCampaignLocations(array);
        }

        if (isProgrammatic && campaignData?.locations?.length > 0) {
            campaignData.locations.forEach((item) => {
                if (item) {
                    array.push({
                        name: `${item.cityName ? item.cityName : ''}, ${item.countyName ? item.countyName : ''}, ${
                            item.stateName ? item.stateName : ''
                        }`,
                        zipCodes: item.expandedZipcodes,
                    });
                }
            });
            setCampaignLocations(array);
        }
    };

    const replaceFileExtension = (filename) => {
        if (filename.endsWith('jpeg')) {
            filename = filename?.replace(/\.jpeg$/, '.jpg');
        }
        return filename;
    };
    useEffect(() => {
        if (campaignData?.locations) {
            formatLocationData();
        }
    }, [campaignData?.locations]);

    const saveGoogleCampaign = async (paymentIntentId) => {
        setCampaignPostData({
            ...campaignPostData,
            isLoading: true,
        });
        try {
            const googlePostBody = {
                campaignTypeId: campaignData.campaignTypeId,
                campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                advertiserId: campaignData.advertiserId,
                agencyId: campaignData.agencyId,
                startDate: campaignData.startDate,
                endDate: campaignData.endDate,
                campaignName: campaignData.campaignName,
                headline: campaignData.headline,
                headline2: campaignData.headline2,
                headline3: campaignData.headline3,
                headline4: campaignData.headline4,
                headline5: campaignData.headline5,
                headline6: campaignData.headline6,
                description: campaignData.description,
                description2: campaignData.description2,
                description3: campaignData.description3,
                description4: campaignData.description4,
                websiteURL: campaignData.websiteURL,
                budget: campaignData.budget,
                stripePaymentId: paymentIntentId ? paymentIntentId : 'Not Applicable',
                googleCampaignKeywords: campaignData.googleCampaignKeywords,
                googleCampaignLocations: campaignLocations,
            };
            //post to google
            const resp = await postNewGoogleCampaign(googlePostBody, 'POST');
            resetFields();
            handleNavButtonNext();
            return resp;
        } catch (err) {
            setCampaignPostData({
                isLoading: false,
                success: false,
                error: true,
                message: ['Something went wrong, Please try again later.'],
            });
            return err;
        }
    };

    const savePontiacCampaign = async (paymentIntentId) => {
        setCampaignPostData({
            ...campaignPostData,
            isLoading: true,
        });
        const formattedFilename = replaceFileExtension(campaignData?.file?.file?.name);
        const formattedBase64 = campaignData?.file.base64.split(',')[1];

        try {
            const pontiacCreativeBody = {
                originalFileName: formattedFilename,
                pontiacCreativeName: campaignData.file.fileName,
                clickURL: campaignData?.websiteURL,
                fileEncoded: formattedBase64,
                advertiserId: campaignData?.advertiserId,
                pontiacCreativeTypeId: campaignData?.file.creativeTypeId,
                pontiacCreativeBannerSizeId: campaignData?.file.pontiacCreativeBannerSizeId,
            };

            const pontiacCreativeResp = await postNewPontiacCreative(pontiacCreativeBody, 'POST');

            if (pontiacCreativeResp.data) {
                const pontiacCreativeId = pontiacCreativeResp.data;

                const pontiacPostBody = {
                    campaignTypeId: campaignData.campaignTypeId,
                    campaignTypeCategoryId: campaignData.campaignTypeCategoryId,
                    advertiserId: campaignData.advertiserId,
                    agencyId: campaignData.agencyId,
                    startDate: campaignData.startDate,
                    endDate: campaignData.endDate,
                    campaignName: campaignData.campaignName,
                    budget: campaignData.budget,
                    stripePaymentId: paymentIntentId ? paymentIntentId : 'Not Applicable',
                    pontiacCreativeId: pontiacCreativeId,
                    pontiacCampaignLocations: campaignLocations,
                };
                //post to pontiac
                const resp = await postNewPontiacCampaign(pontiacPostBody, 'POST');
                resetFields();
                handleNavButtonNext();
                return resp;
            }
        } catch (err) {
            setCampaignPostData({
                isLoading: false,
                success: false,
                error: true,
                message: ['Something went wrong, Please try again later.'],
            });
            return err;
        }
    };

    const saveMobileCampaign = async () => {
        setCampaignPostData({
            ...campaignPostData,
            isLoading: true,
        });

        const body = {
            ...campaignData,
            fullName: profile?.fullName,
            email: profile?.email,
            phoneNumber: profile?.phoneNumber,
            userName: profile?.userName,
        };

        const fileToBeUploaded = campaignData?.file?.file;

        const formData = new FormData();

        if (campaignData?.file?.file) {
            formData.append('file', campaignData.file.file);
        }

        if (campaignData?.locations) {
            formData.append('locations', JSON.stringify(campaignData.locations));
        }

        // Append other data to FormData if needed
        for (const key in body) {
            formData.append(key, body[key]);
        }

        try {
            const resp = await postNewMobileCamapign(formData, 'POST');
            resetFields();
            handleNavButtonNext();
            return resp;
        } catch (err) {
            setCampaignPostData({
                isLoading: false,
                success: false,
                error: true,
                message: ['Something went wrong, Please try again later.'],
            });
        }
        return err;
    };

    const saveNewCampaign = async (paymentIntentId) => {
        if (isGoogle) {
            await saveGoogleCampaign(paymentIntentId);
        } else if (isProgrammatic) {
            await savePontiacCampaign(paymentIntentId);
        } else if (isMobile) {
            await saveMobileCampaign(paymentIntentId);
        }
    };

    const adRoutes = ['/portal/campaign/createACampaign/2', '/portal/campaign/createACampaign/1', '/portal/campaign/createACampaign/26'];

    return (
        <>
            <CampaignContainer>
                <Switch>
                    <Route exact path="/portal/campaign/createACampaign">
                        <CreateACampaign
                            advertisers={advertisers}
                            setAdvertisers={setAdvertisers}
                            campaignData={campaignData}
                            setCampaignData={setCampaignData}
                            currentAdvertiser={currentAdvertiser}
                            setCurrentAdvertiser={setCurrentAdvertiser}
                            setPageIndex={setPageIndex}
                        />
                    </Route>
                    <Route path={adRoutes.map((item) => item)}>
                        <ConnectedDisplayAd
                            campaignData={campaignData}
                            setCampaignData={setCampaignData}
                            advertisers={advertisers}
                            setAdvertisers={setAdvertisers}
                            saveNewCampaign={saveNewCampaign}
                            campaignPostData={campaignPostData}
                            setCampaignPostData={setCampaignPostData}
                            currentAdvertiser={currentAdvertiser}
                            setCurrentAdvertiser={setCurrentAdvertiser}
                            handleNavButtonBack={handleNavButtonBack}
                            handleNavButtonNext={handleNavButtonNext}
                            pageIndex={pageIndex}
                            setPageIndex={setPageIndex}
                        />
                    </Route>

                    <Route path="/portal/campaign/viewCampaigns">
                        <ConnecteViewCampaigns
                            advertisers={advertisers}
                            setAdvertisers={setAdvertisers}
                            campaigns={campaigns}
                            profile={profile}
                            currentAdvertiser={currentAdvertiser}
                        />
                    </Route>
                </Switch>
            </CampaignContainer>
        </>
    );
};

export default ConnectedCampaign;
